import ky from 'ky';

export interface RssFeedSite {
    id: string;
    name: string;
    logo: string;
    url: string;
    rss: string;
}

export interface RssFeedItem {
    id: string;
    title: string;
    link: string;
    pubDate: number;
}

export interface RssFeedInfo {
    site: RssFeedSite;
    items: RssFeedItem[];
    loaded: boolean;
}

const CORS_PROXY = 'https://archive.ni.brain.riken.jp/cors/';
const feedInfoCache = new Map<string, RssFeedInfo>();

// RSS 2.0 `<item>`s and Atom `<entry>`s, read with the browser's own XML parser.
const parseFeed = (xml: string, site: RssFeedSite): RssFeedItem[] => {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    const items: RssFeedItem[] = [];
    doc.querySelectorAll('item, entry').forEach((node) => {
        const text = (selector: string) => node.querySelector(selector)?.textContent?.trim() ?? '';
        const title = text('title');
        const link = text('link') || node.querySelector('link')?.getAttribute('href') || '';
        const dateString = text('pubDate') || text('updated') || text('published') || text('date');
        const pubDate = Math.floor(new Date(dateString).getTime() / 1000);
        if (title !== '' && link !== '' && !Number.isNaN(pubDate)) {
            items.push({ id: site.id, title, link, pubDate });
        }
    });
    return items;
};

const getInfo = async (site: RssFeedSite): Promise<RssFeedInfo> => {
    const info = feedInfoCache.get(site.id) || { site, items: [], loaded: false };
    if (!info.loaded) {
        info.loaded = true;
        try {
            const xml = await ky.get(CORS_PROXY + site.rss).text();
            info.items.push(...parseFeed(xml, site));
        } catch {
            // ignore
        }
        feedInfoCache.set(site.id, info);
    }
    return info;
};

const getSite = (id: string) => {
    const info = feedInfoCache.get(id);
    if (typeof info === 'undefined') {
        return { id: id, name: '', logo: '', url: '' };
    }
    return info.site;
};

const RssFeed = {
    getInfo,
    getSite,
};

export default RssFeed;
